import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useMe } from "@/lib/hooks/use-me";
import { needsHoursSubmission } from "@/lib/shift/hours-submission";

/**
 * How many finished shifts we look back over for hours that were never submitted.
 *
 * "Owes hours" cannot be expressed as a server-side filter: it depends on the shift's time entry
 * being absent or incomplete _and_ on `project.allowEmployeeSubmitHours`, and `ShiftFilterKeys`
 * exposes neither. So we pull a bounded window of finished shifts and decide here.
 *
 * The window is the **most recent** ones — see the sort below. It was `asc` at first, which quietly
 * meant "the 200 oldest shifts this person ever worked": anyone past their first 200 shifts got a
 * badge computed from ancient history and a count of zero for everything they actually owe.
 */
const LOOKBACK_TAKE = 200;

/**
 * The shifts this employee still owes hours for — the "3 diensten wachten op jouw uren" line on
 * Vandaag, the badge on the Afgerond tab, and the action list on Afgerond itself. One query behind
 * all three, so a number and the list under it can never disagree.
 */
export function useOutstandingHours() {
  const { me } = useMe();
  const employeeId = me?.employee?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["shifts", "outstanding-hours", employeeId] as const,
    queryFn: () =>
      extractionWrapper(
        api.shift.getAll({
          pagination: { take: LOOKBACK_TAKE, page: 1 },
          // Newest first: the window has to cover what someone recently worked, not what they
          // worked when they started.
          sort: { field: "startAt", order: "desc" },
          filter: {
            employeeId: { value: employeeId, operator: "equals" },
            // Mirrors use-my-completed-shifts: a shift is "done" once its scheduled end
            // has passed, regardless of whether hours were entered.
            endAt: { value: new Date().toISOString(), operator: "lt" },
          },
          include: {
            project: { select: true },
            userFunction: { select: true },
            position: { select: true },
            shiftTimeEntry: { select: true },
          },
        }),
      ),
    enabled: !!employeeId,
  });

  // Newest first, matching the query — the action list reads best with the freshest shift on top.
  const outstanding = (data?.items ?? []).filter(needsHoursSubmission);

  return {
    /** Every finished shift in the window that still owes hours, newest first. */
    shifts: outstanding,
    count: outstanding.length,
    /** Oldest outstanding shift, for the "oudste: 19 juli" hint — the tail, now that we sort desc. */
    oldest: outstanding.at(-1) ?? null,
    isLoading,
    refetch,
  };
}
